<template>
  <v-row
    justify="center"
  >
    <v-dialog
      v-model="isVisible"
      scrollable
      persistent
      max-width="800px"
    >
      <data-card
        v-if="editItem"
        :title="`${editItem.Id ? isReadOnly ? $t('Common.Button.View') : $t('Common.Button.Edit') : $t('Common.Button.Create')} ${$t(`${parentComponentName}.Form.Car.Title`)}`"
        :icon="$t(`Route.Cars.Icon`)"
        :color="$t(`Route.Cars.Color`)"
        :colored-border="true"
      >
        <template #toolbar-append>
          <v-btn
            :disabled="isSaving"
            icon
            @click="closeDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>

        <template #default>
          <v-card-text
            ref="formContainer"
            class="pt-5"
          >
            <v-form
              ref="form"
              v-model="formValid"
            >
              <v-autocomplete
                v-model="editItem.BrandId"
                :items="carBrands.data"
                :rules="validator.vuetifyFormFieldRules('BrandId')"
                :error-messages="serverErrorMessages['BrandId']"
                :label="$t(`${parentComponentName}.Form.Car.Fields.BrandId.Label`)"
                :hint="$t(`${parentComponentName}.Form.Car.Fields.BrandId.Hint`)"
                :readonly="isReadOnly"
                clearable
                item-text="Name"
                item-value="Id"
                @change="getModels(editItem.BrandId)"
              />

              <v-autocomplete
                v-model="editItem.CarId"
                :items="carModels.data"
                :rules="validator.vuetifyFormFieldRules('CarId')"
                :error-messages="serverErrorMessages['CarId']"
                :label="$t(`${parentComponentName}.Form.Car.Fields.CarId.Label`)"
                :hint="$t(`${parentComponentName}.Form.Car.Fields.CarId.Hint`)"
                :readonly="isReadOnly"
                clearable
                item-text="Name"
                item-value="Id"
              />

              <v-text-field
                v-model="editItem.PlateNumber"
                :rules="validator.vuetifyFormFieldRules('PlateNumber')"
                :error-messages="serverErrorMessages['PlateNumber']"
                :label="$t(`${parentComponentName}.Form.Car.Fields.PlateNumber.Label`)"
                :hint="$t(`${parentComponentName}.Form.Car.Fields.PlateNumber.Hint`)"
                :readonly="isReadOnly"
              />

              <v-switch
                v-model="editItem.Active"
                :rules="validator.vuetifyFormFieldRules('Active')"
                :error-messages="serverErrorMessages['Active']"
                :label="$t(`${parentComponentName}.Form.Car.Fields.Active.Label`)"
                :readonly="isReadOnly"
                color="success"
                inset
              />
            </v-form>
          </v-card-text>
        </template>

        <template #card-actions>
          <v-alert
            :value="showGenericError"
            dense
            tile
            colored-border
            border="top"
            type="error"
            class="ma-0 pt-4 text-center"
          >
            {{ $t('Common.Error.Generic') }}
          </v-alert>
          <v-divider />
          <v-card-actions>
            <div class="flex-grow-1" />
            <v-btn
              :disabled="isSaving"
              text
              @click="closeDialog"
            >
              {{ isReadOnly ? $t('Common.Button.Close') : $t('Common.Button.Cancel') }}
            </v-btn>
            <v-btn
              v-if="!isReadOnly"
              :loading="isSaving"
              :disabled="isSaving"
              color="primary"
              depressed
              @click="saveItem"
              v-text="$t('Common.Button.Save')"
            />
          </v-card-actions>
        </template>
      </data-card>
    </v-dialog>
  </v-row>
</template>

<script>
import Consumers        from '../../api/mixins/Consumers'
import ConsumerCarModel from '../../api/mixins/models/ConsumerCarModel'
import dialogData       from '../../mixins/dialogData'
import DialogVisible    from '../../mixins/dialogVisible'
import DataCard         from '../../components/common/DataCard'
import Auth             from '../../mixins/auth'

export default {
  name      : 'ConsumerCarEditDialog',
  components: { DataCard },
  directives: {},
  mixins    : [Auth, DialogVisible, dialogData, Consumers, ConsumerCarModel],
  props     : {
    consumer: {
      type   : Object,
      default: undefined
    }
  },
  data () {
    return {
      parentComponentName: 'Consumers',
      carBrands          : {
        loading: false,
        data   : []
      },
      carModels: {
        loading: false,
        data   : []
      }
    }
  },
  computed: {
    isReadOnly () {
      return !this.userCan(this.parentComponentName, 'MANAGE') || this.editItem.viewMode
    }
  },
  watch: {
    isVisible (newVal) {
      if (newVal) {
        this.getBrands()
        this.$nextTick(() => {
          this.getModels(this.editItem.BrandId)
        })
      }
    }
  },
  beforeCreate () {
  },
  created () {
  },
  beforeMount () {
  },
  mounted () {
  },
  beforeUpdate () {
  },
  updated () {
  },
  beforeDestroy () {
  },
  destroyed () {
  },
  methods: {
    saveItem () {
      if (this.consumer) this.editItem.ConsumerId = this.consumer.Id

      if (!this.formValidate()) return
      this.isSaving = true

      this.saveCar(this.editItem)
        .then(response => {
          this.handleFormSaveResponse(response)
        })
        .catch(error => {
          this.formSaveError(error)
        })
        .finally(() => {
          this.isSaving = false
        })
    },

    getBrands () {
      this.carBrands.loading = true
      this.allCarBrands({
        itemsPerPage: 1000000,
        orderBy     : 'ASC',
        page        : 1,
        sortBy      : 'Name',
        filter      : {
          Active: this.editItem.Id ? 'all' : 'active'
        }
      })
        .then(response => {
          if (response && response.status === 200 && response.data) {
            this.carBrands.data = response.data.data
          }
        })
        .catch(e => { })
        .finally(() => {
          this.carBrands.loading = false
        })
    },

    getModels (brandId = null) {
      if (!brandId) {
        this.carModels.data = []
        this.editItem.NameId = null
        return
      }
      this.carModels.loading = true
      this.allCarModels({
        itemsPerPage: 1000000,
        orderBy     : 'ASC',
        page        : 1,
        sortBy      : 'Name',
        filter      : {
          Active: this.editItem.Id ? 'all' : 'active',
          Brand : brandId
        }
      })
        .then(response => {
          if (response && response.status === 200 && response.data) {
            this.carModels.data = response.data.data
          }
        })
        .catch(e => { })
        .finally(() => {
          this.carModels.loading = false
        })
    }
  }
}
</script>

<style scoped>

</style>
